import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getAdminAiUsage,
  updateAccountAiCap,
  updateGlobalAiMonthlyCap,
} from "@/lib/admin-ai-usage.functions";

export const Route = createFileRoute("/_authenticated/admin/ai-usage")({
  component: AdminAiUsagePage,
});

type Window = "today" | "7d" | "30d";

function AdminAiUsagePage() {
  const [window, setWindow] = useState<Window>("7d");
  const fetch = useServerFn(getAdminAiUsage);
  const updateAcct = useServerFn(updateAccountAiCap);
  const updateGlobal = useServerFn(updateGlobalAiMonthlyCap);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ai-usage", window],
    queryFn: () => fetch({ data: { window } }),
    refetchInterval: 60_000,
  });

  const [editing, setEditing] = useState<null | {
    accountId: string;
    name: string;
    daily: string;
    monthly: string;
  }>(null);
  const [globalCap, setGlobalCap] = useState<string>("");
  const [globalDialogOpen, setGlobalDialogOpen] = useState(false);

  const acctMut = useMutation({
    mutationFn: (vars: {
      accountId: string;
      dailyCapUsd: number | null;
      monthlyCapUsd: number | null;
    }) => updateAcct({ data: vars }),
    onSuccess: () => {
      toast.success("Account caps updated");
      qc.invalidateQueries({ queryKey: ["admin-ai-usage"] });
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const globalMut = useMutation({
    mutationFn: (cap: number) => updateGlobal({ data: { monthlyCapUsd: cap } }),
    onSuccess: () => {
      toast.success("Global monthly cap updated");
      qc.invalidateQueries({ queryKey: ["admin-ai-usage"] });
      setGlobalDialogOpen(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const t = data.totals;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">AI usage & budget</h2>
          <p className="text-sm text-muted-foreground">
            System-wide AI spend, per-account usage, and budget controls.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1">
          {(["today", "7d", "30d"] as const).map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                window === w
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {w === "today" ? "Today" : `Last ${w}`}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Spend" value={`$${t.cost_usd.toFixed(2)}`} />
        <Kpi label="Calls" value={t.calls.toLocaleString()} />
        <Kpi
          label="Cache hit rate"
          value={`${t.cache_hit_rate.toFixed(1)}%`}
          hint={`${t.cache_hits.toLocaleString()} hits`}
        />
        <Kpi
          label="Tokens"
          value={`${((t.input_tokens + t.output_tokens) / 1000).toFixed(1)}k`}
          hint={`${(t.input_tokens / 1000).toFixed(1)}k in · ${(t.output_tokens / 1000).toFixed(1)}k out`}
        />
      </div>

      {/* Global cap */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Global monthly cap</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Hard ceiling across all accounts this calendar month.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setGlobalCap(String(data.global_monthly.cap_usd));
              setGlobalDialogOpen(true);
            }}
          >
            Edit cap
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
          <span>
            <span className="font-semibold">
              ${data.global_monthly.spend_usd.toFixed(2)}
            </span>{" "}
            <span className="text-muted-foreground">
              / ${data.global_monthly.cap_usd.toFixed(2)}
            </span>
          </span>
          <ProgressBar pct={data.global_monthly.pct_used} />
        </div>
      </div>

      {/* Capability breakdown */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 text-sm font-medium">By capability</div>
        {data.capability_breakdown.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No AI activity in this window.
          </p>
        ) : (
          <div className="space-y-2">
            {data.capability_breakdown.map((c) => {
              const pct =
                t.cost_usd > 0 ? (c.cost_usd / t.cost_usd) * 100 : 0;
              return (
                <div key={c.capability} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">{c.capability}</span>
                    <span className="text-muted-foreground">
                      ${c.cost_usd.toFixed(2)} · {c.calls} calls ·{" "}
                      {c.cache_hits} cache hits
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Per-account table */}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <Th>Account</Th>
              <Th>Plan</Th>
              <Th className="text-right">Window spend</Th>
              <Th className="text-right">Month spend</Th>
              <Th className="text-right">Daily cap</Th>
              <Th className="text-right">Monthly cap</Th>
              <Th className="text-right">% of cap</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.account_spend.map((a) => (
              <tr key={a.account_id} className="hover:bg-muted/20">
                <Td>
                  <Link
                    to="/admin/accounts/$accountId"
                    params={{ accountId: a.account_id }}
                    className="underline-offset-2 hover:underline"
                  >
                    {a.name}
                  </Link>
                </Td>
                <Td>
                  <Badge variant="outline" className="text-xs">
                    {a.plan}
                  </Badge>
                </Td>
                <Td className="text-right">
                  ${a.window_cost_usd.toFixed(2)}
                  <div className="text-xs text-muted-foreground">
                    {a.window_calls} calls
                  </div>
                </Td>
                <Td className="text-right">${a.month_cost_usd.toFixed(2)}</Td>
                <Td className="text-right">
                  {a.daily_cap_usd != null
                    ? `$${a.daily_cap_usd.toFixed(2)}`
                    : ","}
                </Td>
                <Td className="text-right">
                  {a.monthly_cap_usd != null
                    ? `$${a.monthly_cap_usd.toFixed(2)}`
                    : ","}
                </Td>
                <Td className="text-right">
                  {a.monthly_pct_used != null ? (
                    <span
                      className={
                        a.monthly_pct_used >= 0.9
                          ? "text-destructive font-medium"
                          : a.monthly_pct_used >= 0.75
                            ? "text-amber-600 dark:text-amber-400 font-medium"
                            : ""
                      }
                    >
                      {(a.monthly_pct_used * 100).toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground">,</span>
                  )}
                </Td>
                <Td className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setEditing({
                        accountId: a.account_id,
                        name: a.name,
                        daily:
                          a.daily_cap_usd != null
                            ? String(a.daily_cap_usd)
                            : "",
                        monthly:
                          a.monthly_cap_usd != null
                            ? String(a.monthly_cap_usd)
                            : "",
                      })
                    }
                  >
                    Edit caps
                  </Button>
                </Td>
              </tr>
            ))}
            {data.account_spend.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                  No accounts.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit account caps dialog */}
      <Dialog
        open={editing != null}
        onOpenChange={(o) => !o && setEditing(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set AI spend caps</DialogTitle>
            <DialogDescription>
              {editing?.name}, leave blank to use plan defaults / global cap.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="daily-cap">Daily cap (USD)</Label>
              <Input
                id="daily-cap"
                type="number"
                step="0.01"
                min="0"
                placeholder="No cap"
                value={editing?.daily ?? ""}
                onChange={(e) =>
                  setEditing((p) => (p ? { ...p, daily: e.target.value } : p))
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="monthly-cap">Monthly cap (USD)</Label>
              <Input
                id="monthly-cap"
                type="number"
                step="0.01"
                min="0"
                placeholder="No cap"
                value={editing?.monthly ?? ""}
                onChange={(e) =>
                  setEditing((p) =>
                    p ? { ...p, monthly: e.target.value } : p,
                  )
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              disabled={acctMut.isPending}
              onClick={() => {
                if (!editing) return;
                const daily =
                  editing.daily.trim() === "" ? null : Number(editing.daily);
                const monthly =
                  editing.monthly.trim() === ""
                    ? null
                    : Number(editing.monthly);
                if (
                  (daily != null && !Number.isFinite(daily)) ||
                  (monthly != null && !Number.isFinite(monthly))
                ) {
                  toast.error("Enter valid numbers or leave blank");
                  return;
                }
                acctMut.mutate({
                  accountId: editing.accountId,
                  dailyCapUsd: daily,
                  monthlyCapUsd: monthly,
                });
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global cap dialog */}
      <Dialog open={globalDialogOpen} onOpenChange={setGlobalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Global monthly AI cap</DialogTitle>
            <DialogDescription>
              Hard ceiling across all accounts for the current calendar month.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="global-cap">Cap (USD)</Label>
            <Input
              id="global-cap"
              type="number"
              step="0.01"
              min="0"
              value={globalCap}
              onChange={(e) => setGlobalCap(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setGlobalDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={globalMut.isPending}
              onClick={() => {
                const n = Number(globalCap);
                if (!Number.isFinite(n) || n < 0) {
                  toast.error("Enter a valid non-negative number");
                  return;
                }
                globalMut.mutate(n);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  const color =
    clamped >= 90
      ? "bg-destructive"
      : clamped >= 75
        ? "bg-amber-500"
        : "bg-primary";
  return (
    <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
      <div className={`h-full ${color}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function Th({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th className={`px-3 py-2 text-left font-medium ${className}`}>
      {children}
    </th>
  );
}
function Td({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
